import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { decodePhrase, rotateBarcode } from '@fgb/core';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeModule } from 'angularx-qrcode';
import { Subject, switchMap, takeUntil, timer } from 'rxjs';

/**
 * Displays a member barcode as a QR code. When a rotation phrase is supplied the encoded value has
 * a time based token appended to it, so a screenshot stops scanning once the period rolls over.
 * Without a phrase it renders the static barcode.
 */
@Component({
  selector: 'fgb-rotating-barcode',
  standalone: true,
  imports: [CommonModule, QRCodeModule, TranslateModule],
  templateUrl: './rotating-barcode.component.html',
  styleUrls: ['./rotating-barcode.component.scss'],
})
export class RotatingBarcodeComponent implements OnChanges {
  /** The static barcode, used on its own when there is no phrase and as the prefix when there is. */
  @Input() barcode: string;
  /** Rotation phrase, ie. "30 subsolar frost radio . drew". Omit to render a static barcode. */
  @Input() phrase: string;

  /** The value encoded into the QR code, rotated when a phrase is in play. */
  qrData: string;
  secondsLeft: number = 0;
  period: number = 0;
  rotating: boolean = false;

  private destroyRef = inject(DestroyRef);
  private restart$ = new Subject<void>();

  get percentOfRotate() {
    if (!this.period) return 0;
    return Math.floor((this.secondsLeft / this.period) * 100);
  }

  get rotatingClass() {
    if (!this.rotating) return '';

    if (this.secondsLeft <= 5) return 'is-expiring';
    if (this.percentOfRotate < 35) return 'is-amber';
    return 'is-green';
  }

  /** Announced to screen readers only once the code is about to expire. */
  get expiryMessage() {
    if (!this.rotating || this.secondsLeft > 5) return '';
    return 'Barcode is about to refresh';
  }

  /**
   * How long one lap of the rotating border takes. Deliberately not tied to the period, a single lap
   * over 30s reads as barely moving. Time remaining is conveyed by the colour instead, and the sweep
   * quickens once the code is about to refresh.
   */
  get sweepDuration() {
    return this.secondsLeft <= 5 ? '0.8s' : '2s';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.barcode || changes.phrase) {
      this.start();
    }
  }

  /**
   * Points the QR at the static barcode, then starts rotating if the phrase decodes. A phrase we
   * cannot read is not an error worth surfacing, the static barcode still scans.
   */
  private start(): void {
    this.restart$.next();

    this.qrData = this.barcode;
    this.rotating = false;
    this.secondsLeft = 0;
    this.period = 0;

    if (!this.barcode || !this.phrase) return;

    try {
      this.period = decodePhrase(this.phrase).period;
    } catch {
      return;
    }

    this.rotating = true;

    // Tick every second so the countdown stays smooth, but only re-encode the QR when the token
    // actually changes, which is once per period.
    timer(0, 1000)
      .pipe(
        switchMap(() => rotateBarcode(this.barcode, this.phrase)),
        takeUntil(this.restart$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: rotated => {
          this.secondsLeft = Math.max(0, Math.floor((rotated.expires - Date.now()) / 1000));

          if (rotated.barcode !== this.qrData) {
            this.qrData = rotated.barcode;
          }
        },
        error: () => {
          // Fall back to the static barcode rather than leaving a stale rotated value on screen.
          // Happens when the phrase decodes but is not valid base32, so no token can be generated.
          this.qrData = this.barcode;
          this.rotating = false;
          this.secondsLeft = 0;
          this.period = 0;
        },
      });
  }
}
